/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */

import { Analytics } from '@analytics';
import { CircularAnimation } from '@components/CircularAnimation';
import MobileAppBanner from '@components/MobileAppBanner';
import AdminJobsHost from '@components/AdminJobs';
import PipCreditsHost from '@components/PipCredits';
import WhatsNewHost from '@components/WhatsNew';
import { PrivateRoute, TSUserRoute } from '@components/Routes';
import { SignupType } from '@constants';
import DateFnsUtils from '@date-io/date-fns';
import { onMessage } from '@firebase/messaging';
import { Snackbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert as MuiAlert } from '@material-ui/lab';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { messaging } from '@utils/firebase/firebase';
import { GUEST_REVIEWS_PROTOTYPE_ENABLED, getDateFnsLocale } from '@utils/helpers';
import BookingWidget from 'containers/BookingWidget';
import CheckInPage from 'containers/CheckInPage';
import ManifestPage from 'containers/Manifest/Loadable';
import NotFoundPage from 'containers/NotFoundPage/Loadable';
import OrderConfirmationPage from 'containers/OrderConfirmationPage';
import SignInPage from 'containers/SignIn/Loadable';
import SignUpPage from 'containers/SignUp/Loadable';
import SignUpFromInvite from 'containers/SignUpFromInvite/Loadable';
import TapToTipPage from 'containers/TapToTipPage';
import TicketPage from 'containers/TicketPage';
import Balance from 'containers/TipDirectPages/Balance';
import Dashboard from 'containers/TipDirectPages/Dashboard';
import BookNowBar from 'containers/Widgets/BookNowBar';
import BookNowButton from 'containers/Widgets/BookNowButton';
import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { useInjectSaga } from 'utils/injectSaga';
import GlobalStyle from '../../global-styles';
import { getCurrentUser, setLandingUrl } from '../../store/user/actionCreators';
import { requestNotificationPermission } from '../../utils/firebase/service';
import ChannelManagerPage from '../ChannelManager';
import ForgotPassword from '../ForgotPassword';
import KYC from '../KYC';
import MagicLoginPage from '../MagicLoginPage';
import Onboarding from '../Onboarding';
import OnboardingCompletionPage from '../OnboardingCompletionPage';
import PayoutLink from '../PayoutLink';
import PromotionToursPage from '../PromotionToursPage';
import ResetPasswordPage from '../ResetPasswordPage';
import { ReduxConnectedStatusBar } from '../StatusBar';
import SubscriptionPostCheckout from '../SubscriptionPostCheckout';
import TapToTipAssignCard from '../TapToTipAssignCard';
import Setup from '../TipDirectPages/Setup';
import CompanyAccount from '../TipDirectPages/CompanyAccount';
import GuestReviews from '../TipDirectPages/GuestReviews';
import Learn from '../TipDirectPages/Learn';
import TourPhotos from '../TipDirectPages/TourPhotos';
import GuestFeedback from '../TipDirectPages/GuestFeedback';
import QuestionnaireAdmin from '../TipDirectPages/QuestionnaireAdmin';
import TipDirectAgentProfiles from '../TipDirectPages/TipDirectAgentProfiles';
import TipDirectLinkInformation from '../TipDirectPages/TipDirectLinkInformation';
import TipDirectReviews from '../TipDirectPages/TipDirectReviews';
import TippingPayments from '../TipDirectPages/TippingPayments';
import TippingProfile from '../TipDirectPages/TippingProfile';
import TippingResources from '../TipDirectPages/TippingResources';
import TippingGroupPage from '../TippingGroupPage';
import TourAggregate from '../Widgets/TourAggregate';
import saga from './saga';

const ResourceAssign = lazy(() => import('../ResourceAssign'));
const ResourcesEditor = lazy(() => import('../ResourcesEditor'));
const SettingsPage = lazy(() => import('containers/SettingsPage'));
const BookingPage = lazy(() => import('containers/BookingPage'));
const BookingReports = lazy(() => import('containers/BookingReports'));
const PaymentsPage = lazy(() => import('containers/Payments'));
const PromotionsPage = lazy(() => import('containers/Promotions'));
const AdditionalExtraEditor = lazy(() => import('../AdditionalExtraEditor'));
const BookingDetailsPage = lazy(() => import('../BookingDetailsPage'));
const ToursPage = lazy(() => import('containers/Tours'));
const TourEditorPage = lazy(() => import('containers/TourEditor'));
const MessagingPage = lazy(() => import('containers/MessagingPage'));
const ProductImprovementPage = lazy(() => import('containers/ProductImprovementPage'));
const StripeRequirementsThankYou = lazy(() => import('containers/StripeRequirementsThankYou'));


const useStyles = makeStyles(theme => ({
  alert: {
    fontSize: '16px',
    padding: theme.spacing(1),
    fontWeight: '500'
  }
}));

function App({ user, isUserLoading, dispatch, history }) {
  const hasRequestedNotifications = useRef(false);
  const hasRequestedUser = useRef(false);


  const intl = useIntl();
  const classes = useStyles();
  useInjectSaga({ key: 'app', saga });

  const [notification, setNotification] = React.useState(null);
  const [showNotification, setShowNotification] = React.useState(false);

  useEffect(() => {
    dispatch(setLandingUrl(history.location.pathname));
    console.log('dispatching')

    if (!hasRequestedUser.current) {
      hasRequestedUser.current = true;
      dispatch(getCurrentUser());
    }

    // Handle Foreground Notifications
    const unsubscribe = onMessage(messaging, payload => {
      const { title, body } = payload.notification;

      setNotification({ title, body });
      setShowNotification(true);
    });

    // Cleanup the listener on component unmount
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (
      user &&
      Object.keys(user).length > 0 &&
      !hasRequestedNotifications
    ) {
      hasRequestedNotifications = true;
      requestNotificationPermission();
    }
  }, [user]);


  useEffect(() => {
    /*
    // Tawk.to chat box script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://embed.tawk.to/664b248b9a809f19fb32f642/1huaodgnp';
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };*/
  }, []);

  function initializeAnalytics() {
    Analytics.register({
      companyName: user.companyName,
      companyId: user.companyId,
      siteLanguage: user.companyLanguage,
      region: process.env.REGION
    });

    if (user && user.email) {
      Analytics.identify(user.email, user.userId);
      Analytics.addProfileInformation({ lastVisit: Date.now() });
      Analytics.addProfileInformation(
        {
          name: `${user.profile.firstName} ${user.profile.lastName}`,
          email: user.email,
          userId: user.userId,
          companyId: user.companyId,
          role: user.role,
          region: process.env.REGION
        },
        true
      );
      Analytics.addProfileInformation(
        {
          hasMobileApp: !!user.token,
          currency: user.profile.currency,
          hasUnremittedAmount: !!user.unremittedInformation.totalUnremittedAmount
        },
        false
      );
      Analytics.loadAllExperimentsFromCache();
    }
  }

  if (isUserLoading) {
    return null; //TODO: Loader
  }

  function WaitingComponent(Component) {
    return props => (
      <Suspense fallback={CircularAnimation}>
        <Component {...props} />
      </Suspense>
    );
  }

  //TODO: Create private route component and use it
  return (
    <>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={getDateFnsLocale(intl.locale)}>
        <MobileAppBanner />
        <Switch>

          <Route exact path="/signup">
            <SignUpPage
              type={SignupType.TapToTip}
              user={user}
              dispatch={dispatch}
              history={history}
            />
          </Route>
          <Route exact path="/signup/taptotip">
            <SignUpPage
              type={SignupType.TapToTip}
              user={user}
              dispatch={dispatch}
              history={history}
            />
          </Route>
          <Route exact path="/signup/tipdirect">
            <SignUpPage
              type={SignupType.TapToTip}
              user={user}
              dispatch={dispatch}
              history={history}
            />
          </Route>
          <Route exact path="/forgot-password" component={ForgotPassword} />
          <Route exact path="/reset-password" component={ResetPasswordPage} />
          <Route exact path="/magic-login" component={MagicLoginPage} />

          <Route exact path="/signin" component={SignInPage} />
          <Route exact path="/book" component={BookingWidget} />
          <Route exact path="/signupfrominvite/:inviteId" component={SignUpFromInvite} />
          <Route exact path="/booknow" component={BookNowButton} />
          <Route exact path="/booknowbar" component={BookNowBar} />
          <Route exact path="/tour-aggregate" component={TourAggregate} />
          <Route exact path="/bookingproto" component={WaitingComponent(BookingPage)} />
          <Route exact path="/orderconfirmation" component={OrderConfirmationPage} />
          <Route exact path="/ticket/:ticketId" component={TicketPage} />
          <Route exact path="/post-checkout" component={SubscriptionPostCheckout} />
          <Route exact path="/stripe-requirements/thank-you" component={WaitingComponent(StripeRequirementsThankYou)} />
          {/* <Route exact path="/tip/:guideId" component={TippingPage} />
          <Route exact path="/t/g/:guideId" component={TippingPage} /> */}
          {/* <Route exact path="/t/all/g/:companyId" component={TippingGuidesPage} />*/}
          {/* <Route exact path="/t/c/:cardId" component={TippingPage} />
          <Route exact path="/t/g/:guideId/r" component={TippingPage} />
          <Route exact path="/t/c/:cardId/r" component={TippingPage} /> */}


          <TSUserRoute user={user} path="/settings" component={WaitingComponent(SettingsPage)} />
          <TSUserRoute user={user} exact path="/tours" component={WaitingComponent(ToursPage)} />
          <TSUserRoute user={user} exact path="/manifest" component={ManifestPage} />
          <TSUserRoute
            user={user}
            exact
            path="/payments"
            component={WaitingComponent(PaymentsPage)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/promotions"
            component={WaitingComponent(PromotionsPage)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/promotions/:promotionId"
            component={PromotionToursPage}
          />
          <TSUserRoute
            user={user}
            exact
            path="/tour/:tourId/details"
            component={WaitingComponent(TourEditorPage)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/bookings"
            component={WaitingComponent(BookingReports)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/bookings/:bookingId"
            component={WaitingComponent(BookingDetailsPage)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/bookings/:bookingId/edit"
            component={WaitingComponent(BookingDetailsPage)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/additional-extra"
            component={WaitingComponent(AdditionalExtraEditor)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/additional-extra/:id"
            component={WaitingComponent(AdditionalExtraEditor)}
          />
          <TSUserRoute user={user} exact path="/channel-manager" component={ChannelManagerPage} />
          <TSUserRoute user={user} exact path="/check-in/:bookingId" component={CheckInPage} />
          <TSUserRoute
            user={user}
            exact
            path="/resources"
            component={WaitingComponent(ResourcesEditor)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/resources/:resourceId"
            component={WaitingComponent(ResourcesEditor)}
          />
          <TSUserRoute
            user={user}
            exact
            path="/resources/:resourceId/assign"
            component={WaitingComponent(ResourceAssign)}
          />

          <PrivateRoute user={user} exact path="/kyc" component={KYC} />
          <PrivateRoute user={user} exact path="/payoutlink" component={PayoutLink} />
          <PrivateRoute user={user} exact path="/taptotip" component={TapToTipPage} />
          <PrivateRoute user={user} exact path="/tipdirect" component={Dashboard} />
          <PrivateRoute user={user} exact path="/tipdirect/balance" component={Balance} />
          <PrivateRoute user={user} exact path="/tipdirect/dashboard" component={Dashboard} />
          <PrivateRoute user={user} exact path="/tipdirect/payments" component={TippingPayments} />
          <PrivateRoute user={user} exact adminOnly paymentsCompanyOnly path="/tipdirect/stripe" component={CompanyAccount} />
          <PrivateRoute user={user} exact path="/tipdirect/profile" component={TippingProfile} />
          <PrivateRoute user={user} exact path="/tipdirect/reviews" component={TipDirectReviews} />
          {/* Card 304 — every Setup route is adminOnly. The children used to
              be unguarded, which let a guide open the operator Setup page by
              typing the URL. Setup also guards itself in-page, so a new
              sub-route added without adminOnly fails closed. */}
          <PrivateRoute user={user} exact adminOnly path="/tipdirect/setup" component={Setup} />
          <PrivateRoute user={user} exact adminOnly path="/tipdirect/setup/upsell" component={Setup} />
          <PrivateRoute user={user} exact adminOnly path="/tipdirect/setup/social" component={Setup} />
          <PrivateRoute user={user} exact adminOnly path="/tipdirect/setup/custom-link" component={Setup} />
          <PrivateRoute user={user} exact adminOnly path="/tipdirect/setup/tours" component={Setup} />
          <PrivateRoute user={user} exact adminOnly path="/tipdirect/setup/commission" component={Setup} />
          <PrivateRoute
            user={user}
            exact
            adminOnly
            path="/tipdirect/users"
            component={TipDirectAgentProfiles}
          />
          <PrivateRoute
            user={user}
            exact
            path="/tipdirect/tipdirect"
            component={TipDirectLinkInformation}
          />
          <PrivateRoute user={user} exact path="/tipdirect" component={TapToTipPage} />
          <PrivateRoute
            user={user}
            exact
            path="/tipdirect/assign/:cardId"
            component={TapToTipAssignCard}
          />
          <PrivateRoute
            user={user}
            exact
            path="/taptotip/assign/:cardId"
            component={TapToTipAssignCard}
          />
          <PrivateRoute
            user={user}
            exact
            adminOnly
            path="/tipdirect/resources"
            selectedTab={'resources'}
            component={TippingResources}
          />
          <PrivateRoute user={user} exact path="/group/c/:cardId" component={TippingGroupPage} />
          <PrivateRoute user={user} exact path="/group/g/:guideId" component={TippingGroupPage} />
          <PrivateRoute user={user} exact path="/taptotip/:tab" component={TapToTipPage} />
          {/* Must precede the /tipdirect/:tab catch-all or it never matches */}
          <PrivateRoute
            user={user}
            exact
            adminOnly
            path="/tipdirect/product-improvement"
            component={WaitingComponent(ProductImprovementPage)}
          />
          {/* Card 119 — also above the catch-all. */}
          <PrivateRoute user={user} exact path="/tipdirect/learn" component={Learn} />
          {/* Card 305 — same, and guide-facing: no adminOnly. Who can reach it
              is decided by the company's manual tourPhotos switch, which the
              nav honours and the API enforces. */}
          <PrivateRoute user={user} exact path="/tipdirect/tourphotos" component={TourPhotos} />
          {/* Card 323 — the guide's guest feedback QR. Above the catch-all,
              guide-facing like tour photos: the company's questionnaires
              switch decides, and the API enforces it. */}
          <PrivateRoute user={user} exact path="/tipdirect/feedback" component={GuestFeedback} />
          {/* Card 324 — head office's questionnaires and alerts. adminOnly: the
              API refuses guides too, since answers are about their own tours. */}
          <PrivateRoute
            user={user}
            exact
            adminOnly
            path="/tipdirect/guestfeedback"
            component={QuestionnaireAdmin}
          />
          {/* Card 288 — guest review replies and moderation. PROTOTYPE, above
              the catch-all like the rest. adminOnly because a guide must not be
              able to hide a review about themselves. The route is only rendered
              at all while the prototype switch is on, so flipping it off makes
              /tipdirect/guestreviews fall through to the catch-all rather than
              open a half-built screen. The API enforces the same two gates. */}
          {GUEST_REVIEWS_PROTOTYPE_ENABLED && (
            <PrivateRoute
              user={user}
              exact
              adminOnly
              path="/tipdirect/guestreviews"
              component={GuestReviews}
            />
          )}
          <PrivateRoute user={user} exact path="/tipdirect/:tab" component={TapToTipPage} />
          <PrivateRoute user={user} exact path="/onboarding" component={Onboarding} />
          <PrivateRoute
            user={user}
            exact
            path="/onboarding/complete"
            component={OnboardingCompletionPage}
          />
          <PrivateRoute
            user={user}
            exact
            adminOnly
            path="/messaging"
            component={WaitingComponent(MessagingPage)}
          />
          <Route
            exact
            path="/"
            component={({ history }) => {
              history.push('/TipDirect');
              return null;
            }}
          />
          <Route component={NotFoundPage} />
        </Switch>
        {/* Before What's new on purpose: jobs take priority, then the PIP
            credits notice, and What's new waits while either host is checking
            or open (AdminJobs/events.js, PipCredits/events.js). */}
        <AdminJobsHost user={user} />
        <PipCreditsHost user={user} />
        <WhatsNewHost user={user} />
        <Snackbar
          open={showNotification}
          autoHideDuration={3000}
          onClose={() => setShowNotification(false)}
          className={classes.alert}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
          <MuiAlert variant="filled" elevation={6} severity="success" className={classes.alert}>
            <div>{notification?.body}</div>
          </MuiAlert>
        </Snackbar>
        <ReduxConnectedStatusBar />
      </MuiPickersUtilsProvider>
      <GlobalStyle />
    </>
  );
}

function mapStateToProps(state) {
  return {
    user: state.user.user,
    isUserLoading: !state.user.isInitialUserFetched
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps
);

const withHistoryAndConnect = compose(
  withRouter,
  withConnect
);

export default compose(withHistoryAndConnect)(App);
